// BrowserPhone.tsx · v12.10.30 (#1155 #1156 #1157 the call window, the Call back split button, the transfer overlay, the call back block and its player) · 2026-11-03 · VoiceSpectrum AI
//
// The screens of the browser phone, built from the LOCKED prototype UI-LOCK-T18-CALL-BACK-HANDOFF v1.11 (Tushar 11/1: "so call back
// prototype is locked. i have checked it.") - the engine is lib/browserPhone.ts.
//   0a  CallBackButton   - split button: Call back dials the default number; ▾ lists the numbers on the message; "Another number" with the
//                          +1 mask (L2). With the Call back feature OFF this component is never rendered - the old button stays (S1).
//   1a-1d / 3a / 3b / 5b CallWindow - the top bar in the drawer (L1 default), SAME 190 px in every state, nothing jumps (Tushar 11/1:
//                          "the height will remain same so no flickering on the screen"); dialing / ringing as large text in the
//                          transcript box (L3); Recording pill next to the timer, timer coloured by state (L5); keypad inside the box.
//   5a  TransferOverlay  - 460 px, top centre, light shade, Accept (Enter) / Skip (Esc), people pills, countdown (H1).
//   2   CallBackBlock    - the call in the transcript where it happened, indigo, ▶ Play in its header (P1/P1b);
//       CallBackMiniPlayer - the drawer footer's player for it ("Call back 0:00 / 3:12").
import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { buildUrl } from '../lib/api';
import { usePracticeFeature } from '../lib/practiceFeatures';
import { useAccess } from '../lib/useAccess';
import {
  acceptTransfer, callAgain, clock, closeWindow, fmtPhone, getPhone, hangUp, leaveVoicemail, loadStatus, resumeTransfer, sendDigit,
  skipTransfer, startCallBack, startForHandoff, stopPhone, subscribePhone, tickClose, toE164, toggleHold, toggleMute, togglePad,
  type ActiveCall, type PhoneState,
} from '../lib/browserPhone';

export function usePhone(): PhoneState { return useSyncExternalStore(subscribePhone, getPhone, getPhone); }

function useTick(on: boolean) {
  const [, setN] = useState(0);
  useEffect(() => { if (!on) return; const t = window.setInterval(() => setN((n) => n + 1), 1000); return () => window.clearInterval(t); }, [on]);
}

const Ic = {
  phone: <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z" /></svg>,
  hang: <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10.7 13.3a16 16 0 0 0 3.4 2.6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2v3a2 2 0 0 1-2.2 2A19.8 19.8 0 0 1 3.1 5.2 2 2 0 0 1 5.1 3h3" /><line x1="22" y1="2" x2="2" y2="22" /></svg>,
  mic: <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><rect x="9" y="2" width="6" height="12" rx="3" /><path d="M5 10a7 7 0 0 0 14 0M12 19v3" /></svg>,
  micOff: <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><line x1="2" y1="2" x2="22" y2="22" /><path d="M9 9v1a3 3 0 0 0 5.1 2.1M15 9.3V5a3 3 0 0 0-5.9-.6M19 10a7 7 0 0 1-.9 3.4M5 10a7 7 0 0 0 11.8 5.1M12 19v3" /></svg>,
  pause: <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"><rect x="6" y="4" width="4" height="16" rx="1" /><rect x="14" y="4" width="4" height="16" rx="1" /></svg>,
  play: <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"><path d="M7 4l13 8-13 8z" /></svg>,
  pad: <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"><circle cx="6" cy="5" r="1.7" /><circle cx="12" cy="5" r="1.7" /><circle cx="18" cy="5" r="1.7" /><circle cx="6" cy="11" r="1.7" /><circle cx="12" cy="11" r="1.7" /><circle cx="18" cy="11" r="1.7" /><circle cx="6" cy="17" r="1.7" /><circle cx="12" cy="17" r="1.7" /><circle cx="18" cy="17" r="1.7" /><circle cx="12" cy="22" r="1.5" /></svg>,
  caret: <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>,
};

function langLabel(lang?: string): string {
  const l = (lang ?? '').trim();
  if (!l) return '';
  const code = ({ english: 'EN', spanish: 'ES', french: 'FR', german: 'DE' } as Record<string, string>)[l.toLowerCase()] ?? l.slice(0, 2).toUpperCase();
  return `${l.length > 2 ? l : code} · ${code}`;
}

// ── which drawer shows the call window (the rest of the app shows the one-line bar) ──
let slots = 0;
const slotListeners = new Set<() => void>();
function slotCount() { return slots; }
function subscribeSlots(l: () => void) { slotListeners.add(l); return () => { slotListeners.delete(l); }; }

// ═══════════════════════════ host: start-up, presence, overlay, the bar on other screens ═══════════════════════════
/** Mounted once around the signed-in app (App.tsx). Does nothing unless the practice's Call back feature is on. */
export function BrowserPhoneHost({ children }: { children: ReactNode }) {
  const on = usePracticeFeature('call_back');
  const { hasAccess, ready } = useAccess();
  const allowed = hasAccess('phone', 'call') || hasAccess('texts', 'call');
  const phone = usePhone();
  const navigate = useNavigate();
  const shownInDrawer = useSyncExternalStore(subscribeSlots, slotCount, slotCount) > 0;

  useEffect(() => {
    if (!on || !ready || !allowed) return;
    void loadStatus(true).then(() => startForHandoff());
    return () => stopPhone();
  }, [on, ready, allowed]);

  useEffect(() => { const t = window.setInterval(() => tickClose(), 1000); return () => window.clearInterval(t); }, []);

  // 5a keys: Accept (Enter) / Skip (Esc)
  useEffect(() => {
    if (!phone.transfer) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); void acceptTransfer(); }
      else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); skipTransfer(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [phone.transfer]);

  // 5b: the caller's card opens - on another page, go to Messages first (it takes the pending id when it mounts)
  useEffect(() => {
    const go = () => { if (!window.location.pathname.startsWith('/messages')) navigate('/messages'); };
    window.addEventListener('vs:open-message', go);
    return () => window.removeEventListener('vs:open-message', go);
  }, [navigate]);

  return (
    <>
      {children}
      {on && phone.transfer && <TransferOverlay />}
      {on && phone.call && !shownInDrawer && <FloatingCallBar call={phone.call} onOpen={() => navigate('/messages')} />}
    </>
  );
}

function FloatingCallBar({ call, onOpen }: { call: ActiveCall; onOpen: () => void }) {
  useTick(true);
  const live = call.phase === 'connected' || call.phase === 'vmleave' || call.phase === 'machine';
  const t = live && call.connectedAt ? clock((Date.now() - call.connectedAt) / 1000) : call.phase === 'ringing' && call.ringStartedAt ? `ring ${clock((Date.now() - call.ringStartedAt) / 1000)}` : '--:--';
  return (
    <div className="cw cw-float" role="region" aria-label="Call in progress">
      <div className="cw-mini">
        <span className={`cw-dot ${live ? 'live' : call.phase === 'ended' ? 'off' : 'warn'}`} />
        <span className="cw-name">{call.name}</span>
        <span className={`cw-timer ${live ? (call.held ? 'tc-warn' : 'tc-live') : 'tc-warn'}`}>{t}</span>
        <span className="cw-acts2">
          {live && <button type="button" className={`cw-mb${call.muted ? ' on' : ''}`} onClick={toggleMute}>{call.muted ? Ic.micOff : Ic.mic}{call.muted ? 'Unmute' : 'Mute'}</button>}
          <button type="button" className="cw-mb" onClick={onOpen} title="Back to the message">Message</button>
          {call.phase === 'ended' || call.phase === 'noanswer' || call.phase === 'error'
            ? <button type="button" className="cw-mb" onClick={closeWindow}>Close</button>
            : <button type="button" className="cw-mb red" onClick={hangUp}>{Ic.hang}{live ? 'Hang up' : 'Cancel'}</button>}
        </span>
      </div>
    </div>
  );
}

// ═══════════════════════════ 5a: Incoming call transfer ═══════════════════════════
function TransferOverlay() {
  const { transfer } = usePhone();
  if (!transfer) return null;
  const i = transfer.info;
  const total = i?.ringSec || 20;
  const left = transfer.leftSec;
  const all = (i?.strategy ?? 'all') === 'all';
  const people = i?.people ?? [];
  const ini = (n: string) => n.split(/\s+/).filter(Boolean).slice(0, 2).map((x) => x[0]?.toUpperCase()).join('');
  const noAnswer = ({ voicemail: 'route to voicemail', hold: 'keep the caller on hold', end_summary: 'end the call' } as Record<string, string>)[i?.noAnswer ?? ''] ?? (i?.noAnswer || 'route to voicemail');
  return (
    <>
      <div className="px-hobg" aria-hidden="true" />
      <div className="px-ho" role="alertdialog" aria-label="Incoming call transfer">
        <div className="px-ho-h">
          <span className="px-ho-ring">{Ic.phone}</span>
          <div><b>Incoming call transfer</b><small>{i?.agentName || 'The AI assistant'} · AI is transferring a caller to you</small></div>
          <div className="px-ho-cd">
            <svg width="42" height="42"><circle cx="21" cy="21" r="17" stroke="#ffffff40" strokeWidth="4" fill="none" /><circle cx="21" cy="21" r="17" stroke="#fff" strokeWidth="4" fill="none" strokeDasharray="106.8" strokeDashoffset={106.8 * (1 - left / total)} /></svg>
            <span>{left}s</span>
          </div>
        </div>
        <div className="px-ho-b">
          <div className="px-ho-name">{i ? (i.callerName || fmtPhone(i.callerPhone) || 'Caller') : 'Connecting…'}</div>
          {i && <div className="px-ho-meta">{[fmtPhone(i.callerPhone), (i.language || '').toUpperCase(), i.reference ? `#${i.reference}` : '', `on the call ${clock(i.onCallSec)}`].filter(Boolean).join(' · ')}</div>}
          {i?.need && <div className="px-ho-need"><b>What they need</b>{i.need}</div>}
          {people.length > 0 && (
            <div className="px-ho-also">{all ? 'Ringing at once: ' : 'Ring order: '}
              {people.map((p, k) => (
                <span key={k} className={`px-ho-p${p.state === 'ringing' ? ' ring' : p.state === 'skipped' ? ' gone' : p.state === 'accepted' ? ' took' : ''}`}>
                  <i>{ini(p.name)}</i>{all ? (p.me ? 'You' : p.name) : `${p.order} · ${p.me ? 'You' : p.name}`}
                </span>
              ))}
              {!all && <span> → {noAnswer.replace('route to ', '')}</span>}
            </div>
          )}
          <div className="px-ho-note">The caller hears hold music. Ring timeout {total} s · If no one answers → {noAnswer}.</div>
        </div>
        <div className="px-ho-f">
          <button type="button" className="sk" onClick={skipTransfer}>Skip <kbd>Esc</kbd></button>
          <button type="button" className="ok" onClick={() => void acceptTransfer()}>{Ic.phone} Accept <kbd>Enter</kbd></button>
        </div>
      </div>
    </>
  );
}

// ═══════════════════════════ the call window (drawer top bar) ═══════════════════════════
/** Renders the call window when the live call belongs to this message (and hides the one-line bar elsewhere). */
export function CallWindowSlot({ messageId }: { messageId: string }) {
  const { call } = usePhone();
  const mine = !!call && call.messageId === messageId;
  useEffect(() => {
    if (!mine) return;
    slots++; slotListeners.forEach((l) => l());
    return () => { slots--; slotListeners.forEach((l) => l()); };
  }, [mine]);
  return mine && call ? <CallWindow call={call} /> : null;
}

function CallWindow({ call }: { call: ActiveCall }) {
  useTick(true);
  const st = call.phase;
  const live = st === 'connected' || st === 'vmleave';
  const label = ({ mic: 'Microphone', dialing: 'Dialing…', ringing: 'Ringing…', connected: call.kind === 'ho' ? 'Connected · transfer' : 'Connected', machine: 'Voicemail picked up', vmleave: 'Leaving voicemail', noanswer: 'No answer', ended: 'Call ended', error: 'Not connected' } as Record<string, string>)[st];
  const dot = live ? 'live' : st === 'ended' || st === 'noanswer' || st === 'error' ? 'off' : 'warn';
  const talk = call.connectedAt ? clock(((call.endedAt ?? Date.now()) - call.connectedAt) / 1000) : '0:00';
  const ring = call.ringStartedAt ? clock((Date.now() - call.ringStartedAt) / 1000) : '0:00';
  const tc = live ? (call.held ? 'tc-warn' : 'tc-live') : st === 'machine' ? 'tc-warn' : 'tc-off';
  const showTimer = live || st === 'ended' || st === 'machine';
  const dis = !(live || st === 'machine');

  let box: ReactNode;
  if (st === 'mic') box = <div className="cw-big"><b className="off">Allow the microphone</b><span className="cw-bsub">Your browser asks once - the patient hears you through it</span></div>;
  else if (st === 'error') box = <div className="cw-big cw-err"><b className="off">Could not call</b><span className="cw-bsub">{call.errorText}</span></div>;
  else if (st === 'dialing') box = <div className="cw-big"><b>Dialing…</b></div>;
  else if (st === 'ringing') box = <div className="cw-big"><b>Ringing…<span>{ring}</span></b></div>;
  else if (st === 'noanswer') box = <div className="cw-big"><b className="off">{call.outcome === 'busy' ? 'Busy' : `No answer · rang 0:30`}</b><span className="cw-bsub">Nothing is queued - trying again is your call</span></div>;
  else if (st === 'machine') box = <div className="cw-big"><b>Voicemail picked up</b><span className="cw-bsub">Leave a message yourself or hang up - nothing automatic</span></div>;
  else if (st === 'ended') box = <div className="cw-big"><b className="off">Closing in <span>{call.closeIn ?? 5}</span></b><span className="cw-bsub">{call.kind === 'cb' ? `System note added · ${call.note ?? ''}` : call.note}</span></div>;
  else if (call.pad) box = (
    <div className="cw-last cw-padbox">
      <span className="cw-kout" title="Digits sent">{call.digits || ' '}</span>
      <span className="cw-keys">{'123456789*0#'.split('').map((k) => <button type="button" key={k} onClick={() => sendDigit(k)}>{k}</button>)}</span>
    </div>);
  else if (call.kind === 'ho') box = (
    <div className="cw-last cw-whisper"><b>Whisper - only you heard this</b>{call.whisper ? `“${call.whisper}”` : 'The caller is joined to this call.'}
      {call.waiting && <i>The caller is on hold - press Resume when you are ready to start.</i>}</div>);
  else box = (
    // Q2 (live transcript) is not settled (Wed 11/4) - no transcript source for a call back yet, so no lines: what is true is shown.
    <div className="cw-last"><div className="cw-lh">{call.recorded === true ? <><span className="cw-ldot" />Recording</> : 'On the call'}<span className="cw-lang">{langLabel(call.language)}</span></div>
      <div className="cw-l1 wait">The Disclosure line played to the patient first. The call is added to this message when it ends.</div></div>);

  const ctl = (
    <div className="cw-ctl">
      {call.kind === 'ho' && call.waiting ? (
        <button type="button" className="cw-b go" onClick={resumeTransfer}><i>{Ic.play}</i>Resume</button>
      ) : (
        <>
          <button type="button" className={`cw-b${dis ? ' dis' : ''}${call.muted ? ' on' : ''}`} disabled={dis} onClick={toggleMute}><i>{call.muted ? Ic.micOff : Ic.mic}</i>{call.muted ? 'Unmute' : 'Mute'}</button>
          <button type="button" className={`cw-b${dis ? ' dis' : ''}${call.held ? ' on' : ''}`} disabled={dis} onClick={toggleHold}><i>{call.held ? Ic.play : Ic.pause}</i>{call.held ? 'Resume' : 'Hold'}</button>
          <button type="button" className={`cw-b ico${dis ? ' dis' : ''}${call.pad ? ' on' : ''}`} disabled={dis} onClick={togglePad} title="Keypad - for a phone menu or an extension"><i>{Ic.pad}</i></button>
        </>
      )}
    </div>
  );

  let hang: ReactNode;
  if (st === 'noanswer') hang = <div className="cw-hang two"><button type="button" className="cw-n" onClick={closeWindow}>Close</button><button type="button" className="cw-g" onClick={callAgain}>Call again</button></div>;
  else if (st === 'machine') hang = <div className="cw-hang two"><button type="button" className="cw-g" onClick={leaveVoicemail}>Leave voicemail</button><button type="button" onClick={hangUp}>Hang up</button></div>;
  else if (st === 'ended' || st === 'error') hang = <div className="cw-hang two"><button type="button" className="cw-n" onClick={closeWindow}>{st === 'ended' ? 'Close now' : 'Close'}</button></div>;
  else if (st === 'dialing' || st === 'ringing' || st === 'mic') hang = <div className="cw-hang"><button type="button" onClick={hangUp}>{Ic.hang} Cancel</button></div>;
  else hang = <div className="cw-hang"><button type="button" onClick={hangUp}>{Ic.hang} Hang up</button></div>;

  return (
    <div className="cw cw-dock cw-bar cw-t18" role="region" aria-label="Call">
      <div className="cw-top">
        <span className={`cw-dot ${dot}`} />
        <span className="cw-state">{label}</span>
        {showTimer && <span className={`cw-timer ${tc}`}>{talk}</span>}
        {/* v12.10.30 (#1156): only when this call is actually recorded (Record call audio on); a transfer follows the inbound call's own recording */}
        {live && call.kind === 'cb' && call.recorded === true && <span className="cw-recp" title="This call is being recorded">Recording</span>}
        {call.held && live && <span className="cw-hold" title="The patient hears the practice hold sound; your mic is muted">On hold · music playing</span>}
      </div>
      <div className="cw-who">
        <div className="cw-name">{call.name}</div>
        <div className="cw-num"><b>{fmtPhone(call.number)}</b>{call.language ? <> · <span className="cwlang">{langLabel(call.language)}</span></> : null}</div>
        {call.fromLabel && <div className="cw-from">{call.kind === 'cb' ? 'Calling from ' : ''}{call.fromLabel}</div>}
      </div>
      {box}
      <div className="cw-row">{ctl}{hang}</div>
    </div>
  );
}

// ═══════════════════════════ 0a: the Call back split button ═══════════════════════════
export interface CallBackNumber { number: string; label: string }

/**
 * 0a (L2, LOCKED): Call back dials the default number (the message's call-back number, else the caller); ▾ lists the numbers - choosing
 * one dials it; "Another number" is typed with the +1 mask (10 digits; a leading 1 is the country code) and Call (Enter) dials it.
 * The message STATUS is not touched (P2 - Tushar 11/1 "we will keep the message status as is"): today's "set to In progress" stays only
 * while the feature is off.
 */
export function CallBackButton({ messageId, name, language, numbers, variant = 'top', blockedWhy, onPressed }: {
  messageId: string; name: string; language?: string; numbers: CallBackNumber[]; variant?: 'top' | 'foot'; blockedWhy?: string | null; onPressed?: () => void;
}) {
  const { status, call } = usePhone();
  const [open, setOpen] = useState(false);
  const [other, setOther] = useState('');
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => { if (status === null) void loadStatus(); }, [status]);
  useEffect(() => {
    if (!open) return;
    const off = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', off);
    return () => document.removeEventListener('mousedown', off);
  }, [open]);
  const notSetUp = status && status.featureOn && !status.configured ? (status.reason || 'Call back is not set up yet - VoiceSpectrum Support') : null;
  const busy = !!call && call.phase !== 'ended' && call.phase !== 'noanswer' && call.phase !== 'error';
  const why = blockedWhy || notSetUp || (busy ? 'You are on a call - finish it first.' : null);
  const dial = (num: string) => { setOpen(false); onPressed?.(); void startCallBack({ messageId, to: num, name, language }); };
  const def = numbers[0]?.number ?? '';
  const otherE164 = toE164(other);
  const mask = (v: string) => {
    let d = v.replace(/\D+/g, ''); if (d.length === 11 && d[0] === '1') d = d.slice(1); d = d.slice(0, 10);
    return d.length > 6 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : d.length > 3 ? `(${d.slice(0, 3)}) ${d.slice(3)}` : d;
  };
  const mainCls = variant === 'foot' ? 'aw-foot-callback' : 'aw-primary';
  if (why) {
    return (
      <span className="aw-act-blocked cb-split" title={why} tabIndex={0} aria-label={`Call back - not available. ${why}`}>
        <button className={`${mainCls} aw-act-dis`} type="button" disabled aria-disabled="true">{Ic.phone} <span className="aw-btn-label">Call back</span> <span className="aw-kbd">Ctrl+K</span></button>
      </span>
    );
  }
  return (
    <div className={`cb-split cb-split-${variant}`} ref={ref}>
      <button className={`${mainCls} cb-split-main`} type="button" aria-label={`Call back ${fmtPhone(def)}`} data-track="drawer.action.callback"
        title={`Call ${fmtPhone(def)} from the browser (Ctrl+K)`} onClick={() => dial(def)}>
        {Ic.phone} <span className="aw-btn-label">Call back</span> <span className="aw-kbd">Ctrl+K</span>
      </button>
      <button className={`${mainCls} cb-split-caret`} type="button" aria-haspopup="menu" aria-expanded={open} aria-label="Choose the number" onClick={() => setOpen((v) => !v)}>{Ic.caret}</button>
      {open && (
        <div className="cb-menu" role="menu">
          {numbers.map((n) => (
            <button key={n.number} type="button" role="menuitem" className="cb-menu-item" onClick={() => dial(n.number)}>
              <b>{fmtPhone(n.number)}</b><small>{n.label}</small>
            </button>
          ))}
          <div className="cb-menu-other">
            <label htmlFor={`cb-other-${messageId}`}>Another number</label>
            <div className="cb-menu-row">
              <span className="cb-plus1">+1</span>
              <input id={`cb-other-${messageId}`} inputMode="tel" autoComplete="off" placeholder="(555) 010-1234" value={mask(other)}
                onChange={(e) => setOther(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && otherE164) { e.preventDefault(); dial(otherE164); } }} />
              <button type="button" className="cb-menu-call" disabled={!otherE164} onClick={() => otherE164 && dial(otherE164)}>Call <kbd>Enter</kbd></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════ 2: the call back in the transcript + its player ═══════════════════════════
export interface CallBackInfo { id: string; messageId: string; outcome: string; durationSec: number; recorded: boolean; leftVoicemail?: boolean; to?: string; fromLabel?: string; by?: string; endedAt?: string | null }

/** The event the block's ▶ Play sends to the footer player. */
export const CB_PLAY_EVENT = 'vs:cb-play';

const OUT_WORDS: Record<string, string> = { answered: 'Answered', voicemail: 'Voicemail', 'no-answer': 'No answer', busy: 'Busy', cancelled: 'Cancelled', failed: 'Could not connect' };

export function CallBackBlock({ info, at }: { info: CallBackInfo; at?: string | null }) {
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    const on = (e: Event) => { const d = (e as CustomEvent<{ id?: string; playing?: boolean }>).detail; setPlaying(!!d && d.id === info.id && !!d.playing); };
    window.addEventListener('vs:cb-state', on);
    return () => window.removeEventListener('vs:cb-state', on);
  }, [info.id]);
  const recorded = info.recorded && (info.outcome === 'answered' || info.outcome === 'voicemail');
  const when = at ? new Date(at) : null;
  const sub = info.outcome === 'answered' ? 'Answered' : info.outcome === 'no-answer' ? 'Rang 30 s - no answer'
    : info.outcome === 'voicemail' ? (info.leftVoicemail ? 'Voicemail - left a message' : 'Voicemail picked up - hung up without a message')
    : info.outcome === 'busy' ? 'Busy' : info.outcome === 'failed' ? 'Could not be connected' : 'Cancelled before it connected';
  return (
    <div className="px-cb" role="group" aria-label="Call back">
      <div className="px-cb-h">
        <span className="px-cb-ic">{Ic.phone}</span>
        <b>Call back · {info.by || 'Staff'} · {recorded || info.outcome === 'answered' ? clock(info.durationSec) : (OUT_WORDS[info.outcome] ?? info.outcome)}</b>
        {recorded && <><b>·</b><button type="button" className={`px-cb-play${playing ? ' on' : ''}`} title="Play the call back in the player at the bottom left"
          onClick={() => window.dispatchEvent(new CustomEvent(CB_PLAY_EVENT, { detail: { id: info.id, messageId: info.messageId, durationSec: info.durationSec } }))}>{playing ? '❚❚ Pause' : '▶ Play'}</button></>}
        {info.outcome === 'voicemail' && <span className="px-cb-out">Voicemail</span>}
        <span>{when && !isNaN(when.getTime()) ? `${when.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })} · ${when.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} · ` : ''}{info.fromLabel ? `from ${info.fromLabel} · ` : ''}{sub}</span>
      </div>
    </div>
  );
}

/** The drawer footer's player for a call back ("Call back 0:00 / 3:12"). Plays when a block's ▶ Play is pressed; pressing again pauses. */
export function CallBackMiniPlayer({ messageId }: { messageId: string }) {
  const audio = useRef<HTMLAudioElement | null>(null);
  const [cur, setCur] = useState<{ id: string; dur: number } | null>(null);
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(false);
  const say = (id: string | null, p: boolean) => window.dispatchEvent(new CustomEvent('vs:cb-state', { detail: { id, playing: p } }));
  useEffect(() => { audio.current?.pause(); setCur(null); setPlaying(false); say(null, false); }, [messageId]);
  useEffect(() => {
    const on = async (e: Event) => {
      const d = (e as CustomEvent<{ id: string; messageId: string; durationSec: number }>).detail;
      if (!d || d.messageId !== messageId) return;
      const a = audio.current; if (!a) return;
      if (cur?.id === d.id) { if (a.paused) void a.play(); else a.pause(); return; }
      try {
        const r = await api.get<{ url: string }>(`/t2/voice/callbacks/${encodeURIComponent(d.messageId)}/${encodeURIComponent(d.id)}/recording-url`);
        // pause the message's own recording - one thing plays at a time
        document.querySelectorAll('audio').forEach((x) => { if (x !== a) (x as HTMLAudioElement).pause(); });
        a.src = buildUrl(r.url); setCur({ id: d.id, dur: d.durationSec }); setT(0);
        void a.play();
      } catch { window.dispatchEvent(new CustomEvent('vs:toast-cb', {})); }
    };
    window.addEventListener(CB_PLAY_EVENT, on);
    return () => window.removeEventListener(CB_PLAY_EVENT, on);
  }, [messageId, cur]);
  const dur = useMemo(() => (audio.current && isFinite(audio.current.duration) && audio.current.duration > 0 ? audio.current.duration : cur?.dur ?? 0), [cur, t]);
  return (
    <>
      <audio ref={audio} preload="none" hidden
        onTimeUpdate={(e) => setT((e.target as HTMLAudioElement).currentTime)}
        onPlay={() => { setPlaying(true); say(cur?.id ?? null, true); }}
        onPause={() => { setPlaying(false); say(cur?.id ?? null, false); }}
        onEnded={() => { setPlaying(false); say(cur?.id ?? null, false); }} />
      {cur && (
        <div className="aw-mini-player px-mini-cb" role="group" aria-label="Call back playback">
          <button type="button" className="aw-mini-play" aria-label={playing ? 'Pause the call back' : 'Play the call back'}
            onClick={() => { const a = audio.current; if (!a) return; if (a.paused) void a.play(); else a.pause(); }}>
            {playing ? Ic.pause : Ic.play}
          </button>
          <span className="aw-mini-time px-mini-lab">Call back {clock(t)} / {clock(dur)}</span>
        </div>
      )}
    </>
  );
}
